import { useQuery } from "@apollo/client";
import { GET_GENERATION_TEMPLATES } from "../gql";
import type {
  GenerationTemplatesOrderBy,
  GenerationTemplate,
  GenerationTemplatesConnection,
} from "@casey/db-types";

interface UseGenerationTemplatesOptions {
  first?: number;
  after?: string;
  orderBy?: GenerationTemplatesOrderBy[];
  skip?: boolean;
}

interface UseGenerationTemplatesResult {
  templates: GenerationTemplate[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  loadMore: () => void;
  hasMore: boolean;
  totalCount: number;
}

export function useGenerationTemplates(
  organizationId: string,
  options: UseGenerationTemplatesOptions = {},
): UseGenerationTemplatesResult {
  const {
    first = 50,
    after,
    orderBy = ["IS_FAVORITE_DESC", "LAST_USED_AT_DESC", "NAME_ASC"],
    skip,
  } = options;

  const { data, loading, error, refetch, fetchMore } = useQuery<{
    generationTemplates: GenerationTemplatesConnection;
  }>(GET_GENERATION_TEMPLATES, {
    variables: {
      organizationId,
      first,
      ...(after && { after }),
      ...(orderBy && { orderBy }),
    },
    skip: skip || !organizationId,
    errorPolicy: "all",
  });

  const templates = data?.generationTemplates?.nodes || [];
  const pageInfo = data?.generationTemplates?.pageInfo;
  const totalCount = data?.generationTemplates?.totalCount || 0;

  const loadMore = () => {
    if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.generationTemplates) {
            return prev;
          }

          const newNodes = fetchMoreResult.generationTemplates.nodes;
          const newPageInfo = fetchMoreResult.generationTemplates.pageInfo;

          return {
            ...prev,
            generationTemplates: {
              ...prev.generationTemplates!,
              nodes: [...prev.generationTemplates!.nodes, ...newNodes],
              pageInfo: newPageInfo,
            },
          };
        },
      });
    }
  };

  return {
    templates: templates as GenerationTemplate[],
    loading,
    error: error || null,
    refetch,
    loadMore,
    hasMore: pageInfo?.hasNextPage || false,
    totalCount,
  };
}
